import React from 'react';
import { SlaBands } from '../../../interfaces/panel-data';
import { formatDuration } from '../../../utils/format-duration';
import { countOf, RowUnit } from './sla-row-unit';
import './BandBar.scss';

/**
 * One wording for the configured SLA, so the bar, the p95 beside it and the verdict badge cannot name the same
 * number three ways. Exported for those other callers rather than because the bar needs it public.
 */
export const budgetText = (budgetMs: number): string =>
  `configured SLA ${formatDuration(budgetMs)}`;

/** Scan order: best outcome first, absence of judgement last. Also the legend's order. */
const SEGMENTS: Array<{ band: keyof SlaBands; modifier: string; label: string }> = [
  { band: 'within', modifier: 'within', label: 'Within SLA' },
  { band: 'atRisk', modifier: 'at-risk', label: 'At risk' },
  { band: 'breaching', modifier: 'breaching', label: 'Breaching' },
  { band: 'notRegistered', modifier: 'not-registered', label: 'No budget' },
];

interface BandBarProps {
  /** Null means no budget is configured for this group, so there is nothing to band its rows against. */
  bands: SlaBands | null;
  /**
   * What this group's rows are, so a segment says `Breaching: 3 deltas`. The bar is the one place a count
   * appears with no heading above it and no label beside it.
   */
  rowUnit: RowUnit;
  /**
   * The configured SLA the bands were judged against, named on hover and in the accessible name. The panel
   * drew the split and the p95 but never said what either was measured against, so the one number that makes
   * the colours mean anything was only reachable through the exception table.
   *
   * Null is tolerated even though the payload pairs it with null `bands`: the bar states what it was given
   * rather than inventing a budget for a group that has none.
   */
  budgetMs: number | null;
  /** Rendered in place of the track when `bands` is null. */
  noBudgetText: string;
  /** Rendered in place of the track when a budget exists but nothing landed in a band. */
  nothingJudgedText: string;
}

/**
 * How one group's rows sat against their budget: a track split into four proportional segments. A sibling of
 * `StageBar` rather than a mode of it — that one's two segments mean kept and lost and its lost half is
 * hatched, and one component holding both vocabularies would read as neither.
 *
 * With nothing to band it renders a sentence and no track: an empty track reads as *good*.
 */
export const BandBar: React.FC<BandBarProps> = ({
  bands,
  rowUnit,
  budgetMs,
  noBudgetText,
  nothingJudgedText,
}) => {
  if (!bands) {
    return <span className="ovp-bandbar__none">{noBudgetText}</span>;
  }

  const counts = SEGMENTS.map((segment) => bands[segment.band]);
  if (counts.some((count) => !Number.isFinite(count) || count < 0)) {
    // Malformed, unlike the empty group below: there is no proportion to take, so the panel's ErrorBoundary
    // turning this into "failed to load" is the truth. Four zero-width segments would not be.
    throw new Error(`SLA group carried unusable band counts: ${counts.join(', ')}`);
  }

  const total = counts.reduce((sum, count) => sum + count, 0);
  if (total === 0) {
    // A group whose rows reached no band, which the payload does produce: the count comes from the index and
    // the bands from judging each row, so a row with no elapsed — no `metricValue`, or in flight and never
    // fetched — is in the first and not the second.
    return <span className="ovp-bandbar__none">{nothingJudgedText}</span>;
  }

  const present = SEGMENTS.filter((segment) => bands[segment.band] > 0);
  // Appended to every segment rather than carried by the row, because the bar is what a reader hovers and any
  // segment is as likely to be the one under the pointer.
  const budget = budgetMs === null ? '' : ` · ${budgetText(budgetMs)}`;

  return (
    <span
      className="ovp-bandbar"
      role="img"
      aria-label={
        present
          .map((segment) => `${segment.label}: ${countOf(bands[segment.band], rowUnit)}`)
          .join(', ') + budget
      }
    >
      {present.map((segment) => (
        <span
          key={segment.band}
          className={`ovp-bandbar__segment ovp-bandbar__segment--${segment.modifier}`}
          style={{ width: `${(bands[segment.band] / total) * 100}%` }}
          title={`${segment.label}: ${countOf(bands[segment.band], rowUnit)} of ${countOf(
            total,
            rowUnit
          )} judged${budget}`}
          data-testid={`band-segment-${segment.modifier}`}
        />
      ))}
    </span>
  );
};
